import { RefreshButton } from '@bumble/app/components/RefreshButton';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Badge,
  Tooltip,
  IconButton,
} from '@mui/material';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined'
import { DurationLikeObject } from 'luxon';
import { useEffect, useState } from 'react';
import { useApi } from '../hooks/use-api';
import { useQuery } from '@tanstack/react-query';
import Select from 'react-select';
import { useCpe } from './Dashboards/CpeContext';

interface DashboardTimeRangeProps {
  timeValues: { label: string; value: DurationLikeObject, tooltip: string }[];
  selectedTime: DurationLikeObject;
  onCpeChange: (val: any) => void;
  onTimeChange: (e: React.MouseEvent<HTMLElement>, v: DurationLikeObject) => void;
  onRefresh: () => void;
  selectedTab: string;
  disable?: boolean;
}

interface DeviceName {
  name: string;
  id: string;
}

interface DeviceNames {
  cpe: DeviceName[],
  metro: DeviceName[]
}

const DashboardTimeRange = ({
  timeValues,
  selectedTime,
  onCpeChange,
  onTimeChange,
  onRefresh,
  selectedTab,
  disable,
}: DashboardTimeRangeProps) => {
  const [rotateIcon, setRotateIcon] = useState(0);
  const [cpeNames, setCpeNames] = useState<{ id: string, label: string }[]>([]);
  const api = useApi();
  const { data } = useQuery(
    ['dashboarddevicenames'],
    api.dashboardGetDeviceNamesApiV1DashboardGetDeviceNamesGet.bind(null, {}),
    { enabled: true },
  );

  const { cpe, setCpe } = useCpe();

  const infoUrls = {
    '2': 'https://www.bumblebeenet.com/post/wan-link-health',
    '3': 'https://www.bumblebeenet.com/post/wan-link-utilization-dashboard-faq',
    '4': 'https://www.bumblebeenet.com/post/application-visibility-dashboard',
    '5': 'https://www.bumblebeenet.com/post/application-highlights-dashboard-faq',
    '6': 'https://www.bumblebeenet.com/post/bumblebee-ddos-protection-suite'
  }

  useEffect(() => {
    const tmp = data?.data as DeviceNames;
    if (tmp) {
      setCpeNames(([] as DeviceName[]).concat(tmp.cpe, tmp.metro)?.map(n => ({
        label: n.name,
        id: n.id,
      })) || [])
    }
  }, [data])

  const handleRefreshClick = () => {
    setRotateIcon(rotateIcon + 1);
    onRefresh();
  };

  const handleCpeChange = (selectedOption: any) => {
    setCpe({
      cpeName: selectedOption.label,
      nodeId: selectedOption.id,
    });
    onCpeChange(selectedOption);
  };

  const getInfoUrl = (tab: string): string => {
    if (tab === '2') return infoUrls['2'];
    if (tab === '3') return infoUrls['3'];
    if (tab === '4') return infoUrls['4'];
    if (tab === '5') return infoUrls['5'];
    if (tab === '6') return infoUrls['6'];
    return '#';
  };

  const filteredTimeValues = selectedTab === '3'
      ? timeValues
      : timeValues.filter(t => t.label !== 'Month');

  return (
    <>
      <Box sx={{ display: 'inline-flex', gap: 1 }}>
        <Select
          options={cpeNames}
          onChange={handleCpeChange}
          value={cpeNames.find(option => option.label === cpe.cpeName)}
          styles={{
            control: (baseStyles) => ({
              ...baseStyles,
              width: '300px',
            }),
            menu: (baseStyles) => ({
              ...baseStyles,
              zIndex: 999,
            }),
            option: (provided, state) => ({
              ...provided,
              backgroundColor: state.isSelected ? 'white' : state.isFocused ? 'rgba(0, 123, 255, 0.2)' : 'transparent',
              color: 'black',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 123, 255, 0.2)',
                color: '#007bff',
              },
            }),
          }}
        />
        <Badge badgeContent={0} color="secondary">
          <Box
            component="a"
            href={getInfoUrl(selectedTab)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Tooltip arrow title="Info">
              <IconButton color="primary" size="small">
                <InfoOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Badge>
      </Box>
      <Box sx={{ float: 'right', mb: { xs: 5, md: 0 }, display: 'inline-block' }}>
        <RefreshButton onRefresh={handleRefreshClick} disable={disable || false} />
        <Typography sx={{ mr: 10, display: { xs: 'block', md: 'inline' } }}>
          Refresh
        </Typography>
        <ToggleButtonGroup
          value={selectedTime}
          exclusive
          color="primary"
          onChange={onTimeChange}
          aria-label="text alignment"
          sx={{ background: 'white', display: { xs: 'flex', md: 'inline-flex' } }}
        >
          {filteredTimeValues.map(({ label, value, tooltip }) => (
            <ToggleButton
              value={value}
              aria-label={label}
              key={label}
              title={tooltip}
              sx={{
                fontWeight: 700,
                px: { xs: 1, md: 2 },
                py: { xs: 0, md: 1 },
                textTransform: 'none',
                flexGrow: 1,
              }}
            >
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
    </>
  );
};

export default DashboardTimeRange;
